import { useState } from "react";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  light?: boolean;
}

export default function FAQSection({ faqs, title = "Frequently Asked Questions", light }: FAQSectionProps) {
  const [openId, setOpenId] = useState<number | null>(null);

  const bg = light ? "bg-gray-50" : "bg-[#030C15]";
  const labelColor = light ? "text-[#0EA5E9]" : "text-[#7DD3FC]";
  const headingColor = light ? "text-gray-900" : "text-white";
  const cardBg = light ? "bg-white border-gray-200" : "bg-[#071220] border-white/6";
  const cardActiveBg = light ? "bg-white border-[#0EA5E9]/50" : "bg-[#071220] border-[#0EA5E9]/35";
  const qText = light ? "text-gray-900" : "text-white";
  const aText = light ? "text-gray-600" : "text-white";
  const divider = light ? "border-gray-100" : "border-white/15";
  const btnBg = light ? "bg-gray-100 text-gray-400" : "bg-white/10 text-white";
  const btnActiveBg = light ? "bg-[#0EA5E9]/12 text-[#0EA5E9]" : "bg-[#0EA5E9]/15 text-[#0EA5E9]";

  return (
    <section className={`${bg} py-24`}>
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <p className={`font-['Inter'] ${labelColor} text-xs font-semibold uppercase tracking-[0.18em] mb-3`}>
            FAQ
          </p>
          <h2 className={`font-['Playfair_Display'] ${headingColor} text-3xl md:text-4xl font-bold`}>
            {title}
          </h2>
        </div>

        {/* Accordion */}
        <div className="space-y-2.5">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? cardActiveBg : cardBg}`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : faq.id)}
                  className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left cursor-pointer"
                >
                  <span className={`font-['Inter'] ${qText} font-semibold text-[0.9375rem] leading-snug`}>{faq.question}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${isOpen ? btnActiveBg : btnBg}`}>
                    <i className={`text-sm transition-transform duration-300 ${isOpen ? "ri-subtract-line" : "ri-add-line"}`}></i>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-7 pb-6">
                    <div className={`border-t ${divider} pt-4`}>
                      <p className={`font-['Inter'] ${aText} text-sm leading-[1.9]`}>{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
